import { createSelector } from 'reselect';
import { IStore } from '../IStore';
import { Direction, ExchangeRateType } from './../../graphql/types/types.d';
import { EXCHANGE_ORDER_MAX_INTERVAL, EXCHANGE_ORDER_MAX_START_DAY } from './constants';
import {
  EditedCreateExchangeOrder,
  ExchangeOrderValidationErrorType,
} from './exchange-orders-models';
import { diffInDays, isDateInDates, isSameDate } from './helpers';

export const selectEditedExchangeOrder = (state: IStore): EditedCreateExchangeOrder | undefined =>
  state.exchangeOrders.editedExchangeOrder;

export const selectShouldValidate = (state: IStore): boolean => state.exchangeOrders.shouldValidate;
export const selectValidDates = (state: IStore): string[] => state.exchangeOrders.validDates;
export const selectInvestableAccountBalance = (state: IStore): number | undefined => state.exchangeOrders.investableAccountBalance;
export const selectInvestableSecurityAmount = (state: IStore): number | undefined => state.exchangeOrders.investableSecurityAmount;

export const selectSelectableDates = createSelector(
  [selectEditedExchangeOrder, selectValidDates],
  (exchangeOrder, validDates) => {
    const today = new Date();
    const selectableStartDates = validDates.filter((d) => {
      const diff = diffInDays(today, d);
      return diff < EXCHANGE_ORDER_MAX_START_DAY;
    });
    const selectableEndDates = exchangeOrder?.validity_start
      ? validDates.filter((d) => {
        const diff = diffInDays(exchangeOrder.validity_start!, d);
        return diff < EXCHANGE_ORDER_MAX_INTERVAL;
      })
      : [];

    return { selectableStartDates, selectableEndDates };
  },
);

export const selectExchangeOrderIsValid = createSelector(
  [selectEditedExchangeOrder, selectValidDates, selectInvestableAccountBalance, selectInvestableSecurityAmount],
  (exchangeOrder, validDates, accountBalance, ownedSecurityAmount) => {
    if (!exchangeOrder) {
      return false;
    }

    const mandatoryFieldsValid =
      exchangeOrder.direction &&
      exchangeOrder.account &&
      exchangeOrder.security !== undefined &&
      exchangeOrder.count !== undefined &&
      !isNaN(exchangeOrder.count) &&
      exchangeOrder.type &&
      exchangeOrder.validity_start;

    if (!mandatoryFieldsValid) {
      return false;
    }

    if (exchangeOrder.daytrade && !exchangeOrder.daytrade_type) {
      return false;
    }
    if (
      (exchangeOrder.type === ExchangeRateType.LimitPrice ||
        exchangeOrder.type === ExchangeRateType.StopLimit) &&
      (exchangeOrder.limit_price === undefined || isNaN(exchangeOrder.limit_price))
    ) {
      return false;
    }
    if (
      (exchangeOrder.type === ExchangeRateType.StopMarket ||
        exchangeOrder.type === ExchangeRateType.StopLimit) &&
      (exchangeOrder.activation_price === undefined || isNaN(exchangeOrder.activation_price))
    ) {
      return false;
    }

    if (!exchangeOrder.securityDetails) {
      return false;
    }

    /*if (exchangeOrder.daytrade && exchangeOrder.validity_start) {
      const daytradeDay = exchangeOrder.validity_start;
      const isToday = isSameDate(new Date(daytradeDay), new Date());
      const daytradeDayIsValid = isDateInDates(daytradeDay, validDates) && isToday;
      if (!daytradeDayIsValid) {
        return false;
      }
    } else {
      const today = new Date();
      const selectableStartDates = validDates.filter((d) => {
        const diff = diffInDays(today, d);
        return diff < EXCHANGE_ORDER_MAX_START_DAY;
      });
      const selectableEndDates = exchangeOrder?.validity_start
        ? validDates.filter((d) => {
            const diff = diffInDays(exchangeOrder.validity_start!, d);
            return diff < EXCHANGE_ORDER_MAX_INTERVAL;
          })
        : [];
      const startDateIsValid =
        exchangeOrder?.validity_start &&
        isDateInDates(exchangeOrder.validity_start!, selectableStartDates);

      const endDateIsValid = exchangeOrder?.validity_end
        ? exchangeOrder?.validity_end &&
          isDateInDates(exchangeOrder.validity_end!, selectableEndDates)
        : true;

      if (!startDateIsValid || !endDateIsValid) {
        return false;
      }
    }*/

    if (exchangeOrder.direction === Direction.Buy && !accountBalance) {
      return false;
    }
    if (exchangeOrder.direction === Direction.Buy && exchangeOrder.limit_price && exchangeOrder.count) {
      if (exchangeOrder.limit_price * exchangeOrder.count > (accountBalance || 0)) {
        return false;
      }
    }

    if (exchangeOrder.direction === Direction.Sell && !ownedSecurityAmount) {
      return false
    }
    if (exchangeOrder.direction === Direction.Sell && exchangeOrder.count) {
      if (exchangeOrder.count > (ownedSecurityAmount || 0)) {
        return false;
      }
    }

    return true;
  },
);

export const selectExchangeOrderValidationErrors = createSelector(
  [selectEditedExchangeOrder, selectShouldValidate, selectValidDates, selectInvestableAccountBalance, selectInvestableSecurityAmount],
  (exchangeOrder, shouldValidate, validDates, accountBalance, ownedSecurityAmount) => {
    const result: ExchangeOrderValidationErrorType = {};
    if (!shouldValidate) {
      return result;
    }

    if (!exchangeOrder?.direction) {
      result.direction = 'exchangeOrderFormPage.errors.required';
    }
    if (!exchangeOrder?.account) {
      result.account = 'exchangeOrderFormPage.errors.required';
    }
    if (!exchangeOrder?.security) {
      result.security = 'exchangeOrderFormPage.errors.required';
    }
    if (!exchangeOrder?.count) {
      result.count = 'exchangeOrderFormPage.errors.required';
    }
    if (!exchangeOrder?.type) {
      result.exchangeType = 'exchangeOrderFormPage.errors.required';
    }
    if (!exchangeOrder?.validity_start) {
      result.dates = 'exchangeOrderFormPage.errors.required';
    }
    /*if (!exchangeOrder?.validity_end) {
      result.dates = 'exchangeOrderFormPage.errors.required;
    }*/

    if (exchangeOrder?.daytrade && !exchangeOrder?.daytrade_type) {
      result.daytradeType = 'exchangeOrderFormPage.errors.required';
    }
    if (
      (exchangeOrder?.type === ExchangeRateType.LimitPrice ||
        exchangeOrder?.type === ExchangeRateType.StopLimit) &&
      (exchangeOrder.limit_price === undefined || isNaN(exchangeOrder.limit_price))
    ) {
      result.limitPrice = 'exchangeOrderFormPage.errors.required';
    }
    if (
      (exchangeOrder?.type === ExchangeRateType.StopMarket ||
        exchangeOrder?.type === ExchangeRateType.StopLimit) &&
      (exchangeOrder.activation_price === undefined || isNaN(exchangeOrder.activation_price))
    ) {
      result.activationPrice = 'exchangeOrderFormPage.errors.required';
    }

    /*if (exchangeOrder?.daytrade && exchangeOrder.validity_start) {
      const daytradeDay = exchangeOrder.validity_start;
      const isToday = isSameDate(new Date(daytradeDay), new Date());
      const daytradeDayIsValid = isDateInDates(daytradeDay, validDates) && isToday;
      if (!daytradeDayIsValid) {
        result.dates = 'exchangeOrderFormPage.errors.dayTradeDayInvalid';
      }
    } else {
      const today = new Date();
      const selectableStartDates = validDates.filter((d) => {
        const diff = diffInDays(today, d);
        return diff < EXCHANGE_ORDER_MAX_START_DAY;
      });
      const selectableEndDates = exchangeOrder?.validity_start
        ? validDates.filter((d) => {
          const diff = diffInDays(exchangeOrder.validity_start!, d);
          return diff < EXCHANGE_ORDER_MAX_INTERVAL;
        })
        : [];
      const startDateIsValid =
        exchangeOrder?.validity_start &&
        isDateInDates(exchangeOrder.validity_start!, selectableStartDates);

      const endDateIsValid =
        exchangeOrder?.validity_end &&
        isDateInDates(exchangeOrder.validity_end!, selectableEndDates);

      if (
        exchangeOrder?.validity_start &&
        exchangeOrder.validity_end &&
        (!startDateIsValid || !endDateIsValid)
      ) {
        result.dates = 'exchangeOrderFormPage.errors.date';
      }
    }*/

    if (exchangeOrder?.direction === Direction.Buy && !accountBalance) {
      result.count = 'exchangeOrderFormPage.errors.notEnoughBalance';
    }
    if (exchangeOrder?.direction === Direction.Buy && exchangeOrder.limit_price && exchangeOrder.count) {
      if (exchangeOrder.limit_price * exchangeOrder.count > (accountBalance || 0)) {
        result.count = 'exchangeOrderFormPage.errors.notEnoughBalance';
      }
    }

    if (exchangeOrder?.direction === Direction.Sell && !ownedSecurityAmount) {
      result.count = 'exchangeOrderFormPage.errors.notEnoughSecurity';
    }
    if (exchangeOrder?.direction === Direction.Sell && exchangeOrder.count) {
      if (exchangeOrder.count > (ownedSecurityAmount || 0)) {
        result.count = 'exchangeOrderFormPage.errors.notEnoughSecurity';
      }
    }

    return result;
  },
);
